import { Helmet } from "react-helmet-async";

interface SEOProps {
 title: string;
 description: string;
 canonical?: string;
 type?: "website" | "article" | "product";
 image?: string;
 imageAlt?: string;
 author?: string;
 publishedTime?: string;
 modifiedTime?: string;
 keywords?: string[];
 noindex?: boolean;
 structuredData?: object | object[] | null;
 lang?: string;
 path?: string; // Path for generating hreflang URLs
 rawTitle?: boolean; // When true, use title exactly as provided (no appending site name)
 hreflangConfig?: {
 hasArabic?: boolean; // Whether this page has an Arabic version
 };
}

const SITE_NAME = "Flip For Business";
const SITE_URL = "https://www.theflip.app";
const DEFAULT_IMAGE = `${SITE_URL}/images/hero-product-mockup.png`;

// Helper function to generate hreflang URLs
export const generateHreflangUrls = (path: string) => ({
 en: `${SITE_URL}${path}`,
 ar: `${SITE_URL}/ar${path}`,
 default: `${SITE_URL}${path}`,
});

export const SEO = ({
 title,
 description,
 canonical,
 type = "website",
 image = DEFAULT_IMAGE,
 imageAlt = "Flip - Global USDC Payments Platform",
 author = "Flip Company AS",
 publishedTime,
 modifiedTime,
 keywords = [],
 noindex = false,
 structuredData,
 lang = "en",
 path,
 rawTitle = false,
 hreflangConfig,
}: SEOProps) => {
  // Never double-brand: skip the suffix when the title already ends with a Flip brand mention
  const alreadyBranded = /\|\s*Flip(\s+For\s+Business)?\s*$/i.test(title.trim());
  const fullTitle = rawTitle || alreadyBranded || title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
 
 // Auto-generate canonical from path if available, otherwise fall back to window.location
 const canonicalUrl = canonical || (path ? `${SITE_URL}${path}` : SITE_URL);
 
 // Generate hreflang URLs if path is provided and hasArabic is true (or not specified for backwards compat)
 const shouldIncludeHreflang = path && (hreflangConfig?.hasArabic !== false);
 const hreflangUrls = shouldIncludeHreflang ? generateHreflangUrls(path) : null;

 // Default keywords for all pages
 const defaultKeywords = [
 "USDC payments",
 "crypto payments",
 "business payments",
 "freelancer payments",
 "cross-border payments",
 "stablecoin payments",
 "global payments",
 "B2B payments",
 ];

 const allKeywords = [...new Set([...keywords,...defaultKeywords])];

 return (
 <Helmet>
 {/* Primary Meta Tags */}
 <html lang={lang} />
 <title>{fullTitle}</title>
 <meta name="title" content={fullTitle} />
 <meta name="description" content={description} />
 <meta name="keywords" content={allKeywords.join(", ")} />
 <meta name="author" content={author} />
 <link rel="canonical" href={canonicalUrl} />
 
 {noindex && <meta name="robots" content="noindex, nofollow" />}
 {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}

 {/* Hreflang Tags for Multilingual SEO */}
 {hreflangUrls && (
 <>
 <link rel="alternate" hrefLang="en" href={hreflangUrls.en} />
 <link rel="alternate" hrefLang="ar" href={hreflangUrls.ar} />
 <link rel="alternate" hrefLang="x-default" href={hreflangUrls.default} />
 </>
 )}

 {/* Open Graph / Facebook */}
 <meta property="og:type" content={type} />
 <meta property="og:url" content={canonicalUrl} />
 <meta property="og:title" content={fullTitle} />
 <meta property="og:description" content={description} />
 <meta property="og:image" content={image} />
 <meta property="og:image:alt" content={imageAlt} />
 <meta property="og:site_name" content={SITE_NAME} />
 <meta property="og:locale" content={lang === "ar" ? "ar_AE" : "en_US"} />

 {/* Twitter */}
 <meta name="twitter:card" content="summary_large_image" />
 <meta name="twitter:url" content={canonicalUrl} />
 <meta name="twitter:title" content={fullTitle} />
 <meta name="twitter:description" content={description} />
 <meta name="twitter:image" content={image} />
 <meta name="twitter:site" content="@flip_app" />
 <meta name="twitter:creator" content="@flip_app" />

 {/* Article specific */}
 {type === "article" && publishedTime && (
 <meta property="article:published_time" content={publishedTime} />
 )}
 {type === "article" && modifiedTime && (
 <meta property="article:modified_time" content={modifiedTime} />
 )}
 {type === "article" && author && (
 <meta property="article:author" content={author} />
 )}

 {/* Structured Data */}
 {structuredData && Array.isArray(structuredData) ? (
 structuredData.map((schema, index) => (
 <script key={index} type="application/ld+json">
 {JSON.stringify(schema)}
 </script>
 ))
 ) : structuredData ? (
 <script type="application/ld+json">
 {JSON.stringify(structuredData)}
 </script>
 ) : null}
 </Helmet>
 );
};
// Structured data generators
export const generateOrganizationSchema = () => ({
 "@context": "https://schema.org",
 "@type": "Organization",
 name: "Flip for Business",
 url: "https://www.theflip.app",
 logo: "https://www.theflip.app/images/flip-logo.png",
 description: "Crypto-powered payment gateway for businesses. Send USDC payments by phone number.",
 foundingDate: "2023",
 address: {
 "@type": "PostalAddress",
 addressCountry: "NO",
 },
 contactPoint: {
 "@type": "ContactPoint",
 email: "contact@theflip.app",
 contactType: "customer service",
 availableLanguage: ["English", "Arabic"],
 },
 sameAs: [
 "https://www.linkedin.com/company/flip-company-as",
 "https://x.com/The_Flip_App",
 ],
});

export const generateWebsiteSchema = () => ({
 "@context": "https://schema.org",
 "@type": "WebSite",
 name: "Flip For Business",
 url: "https://www.theflip.app",
 description: "Pay Globally With USDC As Simple As Texting",
 potentialAction: {
 "@type": "SearchAction",
 target: "https://www.theflip.app/blog?search={search_term_string}",
 "query-input": "required name=search_term_string",
 },
});

export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: items.map((item, index) => ({
 "@type": "ListItem",
 position: index + 1,
 name: item.name,
 item: item.url,
 })),
});

export const generateArticleSchema = ({
 title,
 description,
 url,
 image,
 author,
 publishedTime,
 modifiedTime,
}: {
 title: string;
 description: string;
 url: string;
 image?: string;
 author: string;
 publishedTime: string;
 modifiedTime?: string;
}) => ({
 "@context": "https://schema.org",
 "@type": "Article",
 headline: title,
 description: description,
 url: url,
 image: image || "https://www.theflip.app/images/hero-product-mockup.png",
 author: {
 "@type": "Person",
 name: author,
 },
 publisher: {
 "@type": "Organization",
 name: "Flip Company AS",
 logo: {
 "@type": "ImageObject",
 url: "https://www.theflip.app/images/flip-logo.png",
 },
 },
 datePublished: publishedTime,
 dateModified: modifiedTime || publishedTime,
 mainEntityOfPage: {
 "@type": "WebPage",
 "@id": url,
 },
});

export const generateFAQSchema = (faqs: { question: string; answer: string }[]) => ({
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: faqs.map((faq) => ({
 "@type": "Question",
 name: faq.question,
 acceptedAnswer: {
 "@type": "Answer",
 text: faq.answer,
 },
 })),
});

export const generateServiceSchema = ({
 name,
 description,
 url,
}: {
 name: string;
 description: string;
 url: string;
}) => ({
 "@context": "https://schema.org",
 "@type": "Service",
 name: name,
 description: description,
 url: url,
 provider: {
 "@type": "Organization",
 name: "Flip Company AS",
 url: "https://www.theflip.app",
 },
 areaServed: {
 "@type": "Place",
 name: "Worldwide",
 },
});

export const generateHowToSchema = ({
 name,
 description,
 totalTime,
 steps,
}: {
 name: string;
 description: string;
 totalTime: string;
 steps: { name: string; text: string; url?: string }[];
}) => ({
 "@context": "https://schema.org",
 "@type": "HowTo",
 name,
 description,
 totalTime,
 step: steps.map((step, index) => ({
 "@type": "HowToStep",
 position: index + 1,
 name: step.name,
 text: step.text,...(step.url ? { url: step.url } : {}),
 })),
});

export const generateSoftwareApplicationSchema = () => ({
 "@context": "https://schema.org",
 "@type": "SoftwareApplication",
 name: "Flip",
 applicationCategory: "FinanceApplication",
 description: "Pay contractors and freelancers worldwide using USDC. Recipients receive USDC to their phone number.",
 url: "https://www.theflip.app",
 offers: {
 "@type": "Offer",
 price: "0",
 priceCurrency: "USD",
 description: "Free to sign up",
 },
 operatingSystem: "Web",
});

/**
 * Speakable schema — flags text on the page that voice assistants
 * (Google Assistant, Alexa) and AI assistants should prefer for spoken
 * responses. Pass an array of CSS selectors that mark the speakable
 * portion of the page (e.g. ["h1", ".faq-answer", "[data-speakable]"]).
 */
export const generateSpeakableSchema = (
 url: string,
 cssSelectors: string[] = ["h1", "[data-speakable]"]
) => ({
 "@context": "https://schema.org",
 "@type": "WebPage",
 url,
 speakable: {
 "@type": "SpeakableSpecification",
 cssSelector: cssSelectors,
 },
});

export default SEO;
